/** One-op strings in the project registry (RFC 0357 S11).
 * Reads accept both legacy Automerge Text and the new scalar encoding. */
import { ImmutableString, isImmutableString } from '@automerge/automerge/slim'

/** `isImmutableString`, not `instanceof`: a value from a second Automerge copy
 *  (the window can load two) fails `instanceof` while being exactly this. */
export function projectString(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  return isImmutableString(value) ? value.val : undefined
}

export function scalarProjectString<T extends string>(value: T): T {
  return new ImmutableString(value) as unknown as T
}

/** Only records and arrays are walked. A Counter, Date, bytes or a string that
 *  is already a scalar is a value, and taking it apart makes it `{ val }`. */
function isRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || isImmutableString(value))
    return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/** Prepare a fresh registry entry; map keys are identities, not CRDT values. */
/**
 * Fields that stay plain strings, the same lists the project move keeps
 * (RFC 0356, desktop `move-project.ts`). Prose is co-edited text. The rest are
 * compared with `===` or found with `includes` by readers that never learned
 * the wrapper: a scalar there is a value that silently never matches.
 */
export const PROJECT_PLAIN_KEYS: ReadonlySet<string> = new Set([
  'body',
  'content',
  'text',
  'description',
  'instructions',
  'sessionIds',
  'dataType',
  'status',
  'priority',
  'completion',
  'branch',
])

/**
 * A field holding the id of another document: `documentId`, `sessionId`,
 * `commentsId`, `sessionIds`. Automerge opens a document only by a string id,
 * so a wrapped one is an id that cannot be opened, and a reader that hands it
 * to `find` throws. The same rule as the project move (RFC 0356, `isPointer`
 * in desktop `move-project.ts`); found running S11 on the Mod project.
 */
export function isProjectPointer(key: string): boolean {
  return /ids?$/i.test(key)
}

/** A key whose value, and everything below it, is written as given. */
function staysPlain(key: string): boolean {
  return PROJECT_PLAIN_KEYS.has(key) || isProjectPointer(key)
}

/** A value as it is written under `key`: scalar strings, except where the
 *  key is one that stays plain, which holds for everything below it. */
export function scalarProjectEntry<T>(value: T, key?: string): T {
  if (key !== undefined && staysPlain(key)) return plainProjectEntry(value)
  if (typeof value === 'string') return scalarProjectString(value) as T
  if (Array.isArray(value))
    return value.map((child) => scalarProjectEntry(child)) as T
  if (isRecord(value))
    return Object.fromEntries(
      Object.entries(value).map(([k, child]) => [
        k,
        scalarProjectEntry(child, k),
      ]),
    ) as T
  return value
}

/** Where each list method takes the values it inserts. */
const INSERTS: Record<string, number> = {
  push: 0,
  unshift: 0,
  fill: 0,
  insertAt: 1,
  splice: 2,
}

/**
 * A draft whose writes store every string as a scalar. Assignments and list
 * inserts are converted with `scalarProjectEntry`; nested maps and lists come
 * back wrapped the same way, so `d.sessionMeta[id].status = 'running'` is a
 * scalar without the writer saying so. Reads are the draft's own.
 */
export function scalarDraft<T extends object>(draft: T, under?: string): T {
  const list = Array.isArray(draft)
  // Inside a plain field everything is written as given.
  if (under !== undefined && staysPlain(under)) return draft
  const handler: ProxyHandler<T> = {
    get(_, key) {
      const value: unknown = Reflect.get(draft, key)
      if (typeof value === 'function') {
        const from = typeof key === 'string' ? INSERTS[key] : undefined
        if (!list) return value
        // Called on the draft: Automerge's `push` is `this.splice`.
        if (from === undefined) return value.bind(draft)
        return (...args: unknown[]) =>
          value.apply(
            draft,
            args.map((arg, i) =>
              i < from ? arg : scalarProjectEntry(arg, under),
            ),
          )
      }
      return Array.isArray(value) || isRecord(value)
        ? scalarDraft(value as object, list ? under : String(key))
        : value
    },
    set(_, key, value) {
      return Reflect.set(
        draft,
        key,
        scalarProjectEntry(value, list ? under : String(key)),
      )
    },
  }
  if (!list) return new Proxy(draft, handler)
  // A list draft cannot be a Proxy target: the engine's invariant check asks
  // it for the descriptor of `push`, and Automerge throws on a key that is not
  // an index. Stand in an empty array and forward everything to the draft.
  return new Proxy([] as unknown as T, {
    ...handler,
    has: (_, key) => Reflect.has(draft, key),
    deleteProperty: (_, key) => Reflect.deleteProperty(draft, key),
    ownKeys: () => Reflect.ownKeys(draft),
    getOwnPropertyDescriptor: (_, key) =>
      key === 'length' || (typeof key === 'string' && /^\d+$/.test(key))
        ? Reflect.getOwnPropertyDescriptor(draft, key)
        : undefined,
  })
}

/**
 * `handle.change` for a project document (RFC 0357 S11): the callback writes
 * through `scalarDraft`, so no string it stores becomes per-character text.
 */
export function changeProject<T>(
  handle: { change(fn: (doc: T) => void, options?: never): void },
  fn: (doc: T) => void,
  options?: unknown,
): void {
  handle.change((doc) => fn(scalarDraft(doc as T & object)), options as never)
}

/** A registry entry with every scalar string read back as a string, for a
 *  reader that compares, keys a Set, or hands the entry to React or a bridge. */
export function plainProjectEntry<T>(value: T): T {
  if (isImmutableString(value)) return value.val as T
  if (Array.isArray(value)) return value.map(plainProjectEntry) as T
  if (isRecord(value))
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        plainProjectEntry(child),
      ]),
    ) as T
  return value
}
